// src/components/reports/ReportResultsModal.tsx
import React from 'react';
import { X, Download, Printer } from 'lucide-react';

interface ReportResultsModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  reportType: string;
  data: any;
  onDownloadPDF?: () => void;
  onDownloadExcel?: () => void;
  isDownloading?: boolean;
}

const ReportResultsModal: React.FC<ReportResultsModalProps> = ({
  isOpen,
  onClose,
  title,
  reportType,
  data,
  onDownloadPDF,
  onDownloadExcel,
  isDownloading = false
}) => {
  if (!isOpen) return null;

  const handlePrint = () => {
    const printContent = document.getElementById('report-content');
    if (printContent) {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>${title}</title>
              <style>
                body { font-family: Arial, sans-serif; margin: 20px; }
                table { width: 100%; border-collapse: collapse; margin: 10px 0; }
                th, td { border: 1px solid #ddd; padding: 8px; text-align: left; vertical-align: top; }
                th { background-color: #f5f5f5; font-weight: bold; }
                .header { margin-bottom: 20px; }
                .section { margin: 20px 0; }
                .section-title { font-size: 18px; font-weight: bold; margin: 15px 0 10px 0; }
                .category-title { font-size: 16px; font-weight: bold; margin: 15px 0 8px 0; color: #333; }
                .subcategory-title { font-size: 14px; font-weight: 600; margin: 10px 0 5px 0; color: #666; text-decoration: underline; }
                .checkbox { text-align: center; font-size: 18px; width: 40px; }
                .item-col { width: 200px; }
                .location-col { width: 150px; }
                .expected-col { width: 80px; text-align: center; }
                .actual-col { width: 80px; text-align: center; }
                .serial-col { width: 120px; }
                .assigned-col { width: 150px; }
                @media print { .no-print { display: none; } }
              </style>
            </head>
            <body>
              ${printContent.innerHTML}
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.print();
      }
    }
  };

  const renderReportContent = () => {
    switch (reportType) {
      case 'INVENTORY_WORKSHEET':
        return <InventoryWorksheetView data={data} />;
      case 'OFFICER_EQUIPMENT':
        return <OfficerEquipmentView data={data} />;
      case 'BUDGET_ANALYSIS':
        return <BudgetAnalysisView data={data} />;
      case 'EQUIPMENT_AUDIT':
        return <EquipmentAuditView data={data} />;
      case 'EXPIRATION_REPORT':
case 'BODY_ARMOR_STATUS':
  return <ExpirationReportView data={data} />;
case 'ANNUAL_ASSIGNMENTS':
  return <AnnualAssignmentView data={data} />;
default:
  return <GenericReportView data={data} />;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center pt-4 pb-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          <div className="flex items-center space-x-2">
            <button
              onClick={handlePrint}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print
            </button>
            
            {onDownloadPDF && (
              <button
                onClick={onDownloadPDF}
                disabled={isDownloading}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <Download className="h-4 w-4 mr-2" />
                PDF
              </button>
            )}
            
            {onDownloadExcel && (
              <button
                onClick={onDownloadExcel}
                disabled={isDownloading}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <Download className="h-4 w-4 mr-2" />
                Excel
              </button>
            )}
            
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          <div id="report-content">
            {renderReportContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

// ✅ FIXED: Show item names but remove "Item Name" header
const InventoryWorksheetView: React.FC<{ data: any }> = ({ data }) => {
  if (!data) {
    return <div className="text-center text-gray-500">No data available</div>;
  }

  return (
    <div className="space-y-6">
      <div className="header">
        <h1 className="text-2xl font-bold">Physical Inventory Worksheet</h1>
        <p className="text-gray-600">Generated: {new Date(data.generatedDate).toLocaleDateString()}</p>
        <div className="mt-2 text-sm text-gray-600">
          <span>Serialized Items: {data.totalSerializedItems || 0}</span>
          <span className="mx-4">•</span>
          <span>Bulk Items: {data.totalBulkItems || 0}</span>
          <span className="mx-4">•</span>
          <span>Total Items: {data.totalItems || 0}</span>
        </div>
      </div>

      {/* ✅ SERIALIZED EQUIPMENT SECTION - Keep "Item Name" header here */}
      {data.serializedItems && data.serializedItems.length > 0 && (
        <div className="section">
          <h2 className="section-title">Serialized Equipment</h2>
          <table className="w-full">
            <thead>
              <tr>
                <th className="checkbox w-12">☐</th>
                <th className="text-left">Item Name</th>
                <th className="serial-col w-32">Serial Number</th>
                <th className="location-col w-40">Location</th>
                <th className="assigned-col w-40">Assigned To</th>
              </tr>
            </thead>
            <tbody>
              {data.serializedItems.map((item: any, itemIndex: number) => (
                <tr key={itemIndex}>
                  <td className="checkbox">☐</td>
                  <td>{item.itemName}</td>
                  <td className="serial-col">{item.serialNumber}</td>
                  <td className="location-col">{item.location}</td>
                  <td className="assigned-col">{item.assignedTo}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* ✅ BULK EQUIPMENT SECTION - Remove "Item Name" header, keep item name data */}
      {data.bulkCategories && data.bulkCategories.length > 0 && (
        <div className="section">
          <h2 className="section-title">Bulk Equipment</h2>
          
          {data.bulkCategories.map((category: any, categoryIndex: number) => (
            <div key={categoryIndex} className="mb-6">
              {/* Category name in BOLD */}
              <h3 className="font-bold text-lg mb-2">{category.categoryName}</h3>
              
              {category.subcategories.map((subcategory: any, subIndex: number) => (
                <div key={subIndex} className="mb-4">
                  {/* Subcategory name UNDERLINED (only if it exists and isn't "Items") */}
                  {subcategory.subcategoryName && (
                    <h4 className="underline font-medium mb-2 ml-4">{subcategory.subcategoryName}</h4>
                  )}
                  
                  {/* Table with item name but no "Item Name" header */}
                  <table className="w-full">
                    <thead>
                      <tr>
                        <th className="item-col w-48 text-left"></th>
                        <th className="location-col w-40 text-left">Location</th>
                        <th className="expected-col w-20 text-center">Expected</th>
                        <th className="actual-col w-20 text-center">Actual</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subcategory.items.map((item: any, itemIndex: number) => (
                        <tr key={itemIndex}>
                          <td className="item-col">{item.itemName}</td>
                          <td className="location-col">{item.location}</td>
                          <td className="expected-col text-center">{item.expectedQuantity}</td>
                          <td className="actual-col text-center border-2 border-blue-300 bg-blue-50">_____</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      
      <div className="mt-8 pt-4 border-t">
        <p className="text-sm text-gray-600">
          Inventory conducted by: _________________________ Date: _________ Signature: _________________________
        </p>
      </div>
    </div>
  );
};

const OfficerEquipmentView: React.FC<{ data: any }> = ({ data }) => {
  if (!data?.officers) {
    return <div className="text-center text-gray-500">No officer equipment data available</div>;
  }

  return (
    <div className="space-y-6">
      <div className="header">
        <h1 className="text-2xl font-bold">Officer Equipment Report</h1>
        <p className="text-gray-600">Generated: {new Date().toLocaleDateString()}</p>
        <p className="text-sm text-gray-600">Total Officers: {data.totalOfficers}</p>
      </div>

      {data.officers.map((officer: any, index: number) => (
        <div key={index} className="section border rounded-lg p-4">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-lg font-semibold">{officer.officerName}</h2>
              <p className="text-sm text-gray-600">
                Badge #{officer.badgeNumber} • {officer.rank} • {officer.assignment}
              </p>
            </div>
            <div className="text-right text-sm">
              <p>Items: {officer.totalItems}</p>
              <p>Value: ${(officer.totalValue || 0).toLocaleString()}</p>
            </div>
          </div>
          
          {officer.equipment.length > 0 ? (
            <table className="w-full">
              <thead>
                <tr>
                  <th>Item Name</th>
                  <th>Serial Number</th>
                  <th>Category</th>
                  <th>Assignment Date</th>
                  <th>Condition</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {officer.equipment.map((item: any, itemIndex: number) => (
                  <tr key={itemIndex}>
                    <td>{item.itemName}</td>
                    <td>{item.serialNumber || 'N/A'}</td>
                    <td>{item.categoryName}</td>
                    <td>{new Date(item.assignmentDate).toLocaleDateString()}</td>
                    <td>{item.condition}</td>
                    <td>${(item.unitValue || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-gray-500 italic">No equipment currently assigned</p>
          )}
        </div>
      ))}
    </div>
  );
};

const BudgetAnalysisView: React.FC<{ data: any }> = ({ data }) => {
  if (!data?.categories) {
    return <div className="text-center text-gray-500">No budget analysis data available</div>;
  }

  return (
    <div className="space-y-6">
      <div className="header">
        <h1 className="text-2xl font-bold">Budget Analysis Report</h1>
        <p className="text-gray-600">
          Period: {new Date(data.reportPeriod.startDate).toLocaleDateString()} - {new Date(data.reportPeriod.endDate).toLocaleDateString()}
        </p>
        <div className="mt-2 text-sm">
          <span>Current Total: ${(data.currentTotal || 0).toLocaleString()}</span>
          {data.previousTotal && (
            <>
              <span className="mx-4">•</span>
              <span>Previous Total: ${data.previousTotal.toLocaleString()}</span>
              <span className="mx-4">•</span>
              <span className={data.percentChange >= 0 ? 'text-red-600' : 'text-green-600'}>
                Change: {data.percentChange > 0 ? '+' : ''}{data.percentChange.toFixed(1)}%
              </span>
            </>
          )}
        </div>
      </div>

      <div className="section">
        <h2 className="section-title">Spending by Category</h2>
        <table className="w-full">
          <thead>
            <tr>
              <th>Category</th>
              <th>Items</th>
              <th>Total Spent</th>
              <th>Average Cost</th>
              <th>% of Total</th>
            </tr>
          </thead>
          <tbody>
            {data.categories.map((category: any, index: number) => (
              <tr key={index}>
                <td>{category.categoryName}</td>
                <td className="text-center">{category.itemCount}</td>
                <td>${category.totalSpent.toLocaleString()}</td>
                <td>${category.averageItemCost.toLocaleString()}</td>
                <td className="text-center">
                  {((category.totalSpent / data.currentTotal) * 100).toFixed(1)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const EquipmentAuditView: React.FC<{ data: any }> = ({ data }) => {
  if (!data?.items) {
    return <div className="text-center text-gray-500">No audit data available</div>;
  }

  return (
    <div className="space-y-6">
      <div className="header">
        <h1 className="text-2xl font-bold">Equipment Audit Report</h1>
        <p className="text-gray-600">Report Type: {data.reportType}</p>
        <p className="text-sm text-gray-600">
          Total Items: {data.totalItems} • Assigned: {data.assignedItems} • Available: {data.availableItems}
        </p>
      </div>

      <div className="section">
        <table className="w-full">
          <thead>
            <tr>
              <th>Item Name</th>
              <th>Serial Number</th>
              <th>Category</th>
              <th>Status</th>
              <th>Assigned To</th>
              <th>Location</th>
              <th>Condition</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((item: any, index: number) => (
              <tr key={index}>
                <td>{item.itemName}</td>
                <td>{item.serialNumber}</td>
                <td>{item.categoryName}</td>
                <td>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    item.status === 'Available' ? 'bg-green-100 text-green-800' :
                    item.status === 'Assigned' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {item.status}
                  </span>
                </td>
                <td>{item.assignedToName || 'Unassigned'}</td>
                <td>{item.location}</td>
                <td>{item.condition}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ExpirationReportView: React.FC<{ data: any }> = ({ data }) => {
  if (!data) {
    return <div className="text-center text-gray-500">No expiration data available</div>;
  }

  return (
    <div className="space-y-6">
      <div className="header">
        <h1 className="text-2xl font-bold">Equipment Expiration Report</h1>
        <p className="text-gray-600">Look-ahead Period: {data.reportPeriod} days</p>
        <div className="mt-2 text-sm text-gray-600">
          <span className="text-red-600">Expired: {data.expired?.length || 0}</span>
          <span className="mx-4">•</span>
          <span className="text-orange-600">Expiring Soon: {data.expiringSoon?.length || 0}</span>
          <span className="mx-4">•</span>
          <span className="text-yellow-600">Warning: {data.warning?.length || 0}</span>
        </div>
      </div>

      {/* Expired Items */}
      {data.expired && data.expired.length > 0 && (
        <div className="section">
          <h2 className="section-title text-red-600">Expired Items</h2>
          <table className="w-full">
            <thead>
              <tr>
                <th>Item Name</th>
                <th>Serial Number</th>
                <th>Expiration Date</th>
                <th>Days Overdue</th>
                <th>Assigned To</th>
                <th>Location</th>
              </tr>
            </thead>
            <tbody>
              {data.expired.map((item: any, index: number) => (
                <tr key={index} className="bg-red-50">
                  <td>{item.itemName}</td>
                  <td>{item.serialNumber || 'N/A'}</td>
                  <td>{new Date(item.expirationDate).toLocaleDateString()}</td>
                  <td className="text-red-600 font-medium">{Math.abs(item.daysUntilExpiration)}</td>
                  <td>{item.assignedToName || 'Unassigned'}</td>
                  <td>{item.location}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Expiring Soon */}
      {data.expiringSoon && data.expiringSoon.length > 0 && (
        <div className="section">
          <h2 className="section-title text-orange-600">Expiring Soon (≤30 days)</h2>
          <table className="w-full">
            <thead>
              <tr>
                <th>Item Name</th>
                <th>Serial Number</th>
                <th>Expiration Date</th>
                <th>Days Until Expiration</th>
                <th>Assigned To</th>
                <th>Location</th>
              </tr>
            </thead>
            <tbody>
              {data.expiringSoon.map((item: any, index: number) => (
                <tr key={index} className="bg-orange-50">
                  <td>{item.itemName}</td>
                  <td>{item.serialNumber || 'N/A'}</td>
                  <td>{new Date(item.expirationDate).toLocaleDateString()}</td>
                  <td className="text-orange-600 font-medium">{item.daysUntilExpiration}</td>
                  <td>{item.assignedToName || 'Unassigned'}</td>
                  <td>{item.location}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Warning Items */}
      {data.warning && data.warning.length > 0 && (
        <div className="section">
          <h2 className="section-title text-yellow-600">Warning (31-90 days)</h2>
          <table className="w-full">
            <thead>
              <tr>
                <th>Item Name</th>
                <th>Serial Number</th>
                <th>Expiration Date</th>
                <th>Days Until Expiration</th>
                <th>Assigned To</th>
                <th>Location</th>
              </tr>
            </thead>
            <tbody>
              {data.warning.map((item: any, index: number) => (
                <tr key={index} className="bg-yellow-50">
                  <td>{item.itemName}</td>
                  <td>{item.serialNumber || 'N/A'}</td>
                  <td>{new Date(item.expirationDate).toLocaleDateString()}</td>
                  <td className="text-yellow-600 font-medium">{item.daysUntilExpiration}</td>
                  <td>{item.assignedToName || 'Unassigned'}</td>
                  <td>{item.location}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
const AnnualAssignmentView: React.FC<{ data: any }> = ({ data }) => {
  if (!data?.assignments) {
    return <div className="text-center text-gray-500">No assignment data available</div>;
  }

  return (
    <div className="space-y-6">
      <div className="header">
        <h1 className="text-2xl font-bold">Annual Assignment Report</h1>
        <p className="text-gray-600">
          Period: {new Date(data.reportPeriod.startDate).toLocaleDateString()} - {new Date(data.reportPeriod.endDate).toLocaleDateString()}
        </p>
        <div className="mt-2 text-sm text-gray-600">
          <span>Total Assignments: {data.totalAssignments}</span>
          <span className="mx-4">•</span>
          <span>Total Value: ${(data.totalValue || 0).toLocaleString()}</span>
        </div>
      </div>

      {/* By Category Summary */}
      {data.byCategory && data.byCategory.length > 0 && (
        <div className="section">
          <h2 className="section-title">Assignments by Category</h2>
          <table className="w-full">
            <thead>
              <tr>
                <th>Category</th>
                <th>Count</th>
                <th>Total Value</th>
                <th>% of Total</th>
              </tr>
            </thead>
            <tbody>
              {data.byCategory.map((category: any, index: number) => (
                <tr key={index}>
                  <td>{category.categoryName}</td>
                  <td className="text-center">{category.count}</td>
                  <td>${category.totalValue.toLocaleString()}</td>
                  <td className="text-center">
                    {((category.count / data.totalAssignments) * 100).toFixed(1)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* By Month Summary */}
      {data.byMonth && data.byMonth.length > 0 && (
        <div className="section">
          <h2 className="section-title">Monthly Assignment Trends</h2>
          <table className="w-full">
            <thead>
              <tr>
                <th>Month</th>
                <th>Assignments</th>
                <th>Total Value</th>
              </tr>
            </thead>
            <tbody>
              {data.byMonth.map((month: any, index: number) => (
                <tr key={index}>
                  <td>{month.month}</td>
                  <td className="text-center">{month.count}</td>
                  <td>${month.totalValue.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* All Assignments */}
      <div className="section">
        <h2 className="section-title">All Assignments</h2>
        <table className="w-full">
          <thead>
            <tr>
              <th>Date</th>
              <th>Officer</th>
              <th>Item</th>
              <th>Serial Number</th>
              <th>Category</th>
              <th>Reason</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            {data.assignments.map((assignment: any, index: number) => (
              <tr key={index}>
                <td>{new Date(assignment.assignmentDate).toLocaleDateString()}</td>
                <td>{assignment.officerName} (#{assignment.badgeNumber})</td>
                <td>{assignment.itemName}</td>
                <td>{assignment.serialNumber || 'N/A'}</td>
                <td>{assignment.categoryName}</td>
                <td>{assignment.assignmentReason}</td>
                <td>${assignment.itemValue.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};


const GenericReportView: React.FC<{ data: any }> = ({ data }) => {
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Report Data</h2>
      <pre className="bg-gray-100 p-4 rounded text-sm overflow-auto max-h-96">
        {JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
};

export default ReportResultsModal;